// Types
import { DeviceType } from '../../../types/DeviceType';
import DisplayLabels from '../labels/DisplayLabels';

// Utils
import { linkTo } from '../../../utils';
import { dateHeader, formatCellDate } from '../../../utils/columns/timeModeCell';

// Services
import AuthService from '../../../services/auth.service';

// Redux
import { SettingsState } from '../../../redux/slices/settingsSlice';

// Common
import { ColumnDef } from '@tanstack/table-core';
import { DeviceEnriched } from '../../../types/DeviceEnriched';

type SuperSentinelStatus = 'active' | 'inactive' | 'not_supported';

const getSuperSentinelStatus = (device: DeviceEnriched): SuperSentinelStatus => {
  if (!device.isSuperSentinelSupported) return 'not_supported';
  return device.isSuperSentinelEnabled ? 'active' : 'inactive';
};

const SUPER_SENTINEL_STYLE: Record<SuperSentinelStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: '#2e7d32' },
  inactive: { label: 'Inactive', color: '#e65100' },
  not_supported: { label: 'Not Supported', color: '#757575' },
};

export const makeDeviceListColumn = (
  onDetail: (device: DeviceEnriched) => void,
  deviceTypes: DeviceType[] | undefined,
  settingStates: SettingsState,
): ColumnDef<DeviceEnriched>[] => {
  const isExternalUser = AuthService.getStoredUserProfile()?.isInternal === false;

  return [
    {
      header: 'DeviceId',
      accessorKey: 'deviceId',
      enableColumnFilter: true,
      size: 150,
      cell: ({ row }) => linkTo(isExternalUser ? '/visits' : '/devices', row.original.deviceId, row.original.deviceId),
    },
    {
      header: 'Type',
      accessorKey: 'typeId',
      enableColumnFilter: false,
      size: 230,
      cell: ({ row }) => {
        return <>{row.original && <>{deviceTypes?.find((d) => d.id == row.original.typeId)?.type}</>}</>;
      },
    },
    {
      header: 'Type Name',
      accessorKey: 'typeName',
      enableSorting: false,
      enableColumnFilter: false,
      size: 180,
      cell: ({ row }) => {
        return <>{row.original && <>{deviceTypes?.find((deviceType) => deviceType.id == row.original.typeId)?.name}</>}</>;
      },
    },
    {
      header: 'Tag Health',
      enableSorting: false,
      enableColumnFilter: false,
      size: 100,
      cell: ({ row }) => (
        <span
          title={
            row.original.isDeviceHealthy ? 'Healthy' : (row.original.deviceNotHealthyReasons ?? []).map((reason: any) => reason.value).join(', ')
          }
        >
          {row.original.isDeviceHealthy ? 'Healthy' : 'Unhealthy'}
        </span>
      ),
    },
    // Super Sentinel and Manufacturer are internal-only.
    ...(isExternalUser
      ? []
      : [
          {
            header: 'Super Sentinel',
            accessorKey: 'isSuperSentinelEnabled',
            enableSorting: false,
            enableColumnFilter: false,
            size: 150,
            cell: ({ row }) => {
              const status = getSuperSentinelStatus(row.original);
              const { label, color } = SUPER_SENTINEL_STYLE[status];
              return (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 10px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    backgroundColor: `${color}22`, // ~13% opacity background
                    color: color,
                    border: `1px solid ${color}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </span>
              );
            },
          },
          {
            header: 'Manufacturer',
            accessorKey: 'manufacturer',
            enableSorting: true,
            enableColumnFilter: true,
            size: 180,
            cell: ({ row }) => <span>{row.original.type.manufacturer}</span>,
          },
        ]),
    {
      header: 'Secondary Device ID',
      accessorKey: 'secondaryDeviceId',
      enableSorting: true,
      enableColumnFilter: true,
      size: 180,
      cell: ({ row }) => <span>{row.original.secondaryDeviceId}</span>,
    },
    {
      header: 'Battery Voltage',
      enableSorting: false,
      enableColumnFilter: false,
      size: 120,
      cell: ({ row }) => row.original.batteryVoltage,
    },
    {
      header: 'Station',
      accessorKey: 'stationIataCode',
      enableSorting: false,
      enableColumnFilter: false,
      size: 180,
      cell: ({ row }) => <span>{row.original.stationIataCode}</span>,
    },
    {
      header: 'Facility',
      accessorKey: 'facilityName',
      enableSorting: false,
      enableColumnFilter: false,
      size: 180,
      cell: ({ row }) => <span>{row.original.facilityName}</span>,
    },
    {
      header: dateHeader('Last Seen'),
      id: 'Last Seen',
      enableSorting: false,
      enableColumnFilter: false,
      size: 120,
      cell: ({ row, table }) => formatCellDate(table, row.original.lastSeenDateTime),
    },
    {
      header: 'Equipment',
      accessorKey: 'serialNumber',
      enableSorting: false,
      enableColumnFilter: false,
      size: 150,
      cell: ({ row }) => {
        return row.original.currentPairing
          ? linkTo('/equipment', row.original.currentPairing.equipmentId ?? '', row.original.currentPairing.serialNumber ?? '')
          : '';
      },
    },
    {
      header: 'Paring Verification Status',
      accessorKey: 'equipmentPairingStatusDescr',
      enableSorting: false,
      enableColumnFilter: false,
      size: 180,
      cell: ({ row }) => {
        const pairingStatus = row.original.currentPairing?.pairingValidationStatus;

        const matchedStatus = settingStates.pairingValidationStatus?.value.find((status) => status.text === pairingStatus);

        return <span>{matchedStatus && matchedStatus.value}</span>;
      },
    },
    {
      header: 'ShipmentNbr',
      accessorKey: 'shipmentNbr',
      enableColumnFilter: true,
      size: 180,
    },
    {
      header: dateHeader('Manufacture DateTime'),
      accessorKey: 'manufactureDateTime',
      enableColumnFilter: false,
      size: 120,
      cell: ({ row, table }) => formatCellDate(table, row.original.manufactureDateTime),
    },
    {
      header: dateHeader('Created'),
      accessorKey: 'createdDateTime',
      size: 110,
      cell: ({ row, table }) => formatCellDate(table, row.original.createdDateTime),
    },
    {
      header: dateHeader('Modified'),
      accessorKey: 'modifiedDateTime',
      size: 110,
      cell: ({ row, table }) => formatCellDate(table, row.original.modifiedDateTime),
    },
    {
      header: 'Labels',
      accessorKey: 'labels',
      enableSorting: false,
      enableColumnFilter: true,
      cell: ({ row }) => <DisplayLabels labels={row.original.labels} />,
    },
  ];
};
